import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Users, Calendar, List, Settings as SettingsIcon, LogOut, BarChart3, CalendarDays, MousePointerClick, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import TableManagement from "../components/dashboard/TableManagement";
import ReservationsList from "../components/dashboard/ReservationsList";
import TableAssignment from "../components/dashboard/TableAssignment";
import GuestCRM from "../components/dashboard/GuestCRM";
import WaitlistView from "../components/dashboard/WaitlistView";
import ShiftSettings from "../components/dashboard/ShiftSettings";
import Settings from "../components/dashboard/Settings";
import Statistics from "../components/dashboard/Statistics";
import CalendarCapacity from "../components/dashboard/CalendarCapacity";
import FloorPlanOverview from "../components/dashboard/FloorPlanOverview";
import AdminLogin from "../components/dashboard/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/admin/verify?token=${token}`);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_email");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token) => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(`${API}/admin/logout?token=${token}`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    setIsAuthenticated(false);
    toast.success("Uitgelogd");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-brand-gold text-xl">Laden...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const navigation = [
    { name: t('dashboard.tables'), path: '/admin', icon: LayoutDashboard, testId: 'nav-tables' },
    { name: 'Toewijzen', path: '/admin/assign', icon: MousePointerClick, testId: 'nav-assign' },
    { name: 'Zaalplan', path: '/admin/floorplan', icon: MapIcon, testId: 'nav-floorplan' },
    { name: t('dashboard.reservations'), path: '/admin/reservations', icon: Calendar, testId: 'nav-reservations' },
    { name: 'Kalender', path: '/admin/calendar', icon: CalendarDays, testId: 'nav-calendar' },
    { name: t('dashboard.guests'), path: '/admin/guests', icon: Users, testId: 'nav-guests' },
    { name: t('dashboard.waitlist'), path: '/admin/waitlist', icon: List, testId: 'nav-waitlist' },
    { name: 'Statistieken', path: '/admin/statistics', icon: BarChart3, testId: 'nav-statistics' },
    { name: 'Instellingen', path: '/admin/settings', icon: SettingsIcon, testId: 'nav-settings' },
  ];

  return (
    <div className="h-screen w-full flex overflow-hidden bg-[#0A0A0A]">
      <aside className="w-64 border-r border-white/10 bg-[#121212] flex flex-col" data-testid="dashboard-sidebar">
        <div className="p-6 border-b border-white/10">
          <h1 className="text-2xl font-serif text-brand-gold" data-testid="dashboard-title">BARBAAR</h1>
          <p className="text-sm text-gray-400">{t('dashboard.title')}</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive 
                    ? 'bg-brand-gold text-black font-semibold' 
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={item.testId}
              >
                <Icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-gray-500 mb-2 truncate">
            {localStorage.getItem("admin_email")}
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-white/20 text-gray-300 hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Uitloggen
          </Button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden bg-[#0A0A0A]">
        <Routes>
          <Route path="/" element={<TableManagement />} />
          <Route path="/assign" element={<TableAssignment />} />
          <Route path="/floorplan" element={<FloorPlanOverview />} />
          <Route path="/reservations" element={<ReservationsList />} />
          <Route path="/calendar" element={<CalendarCapacity />} />
          <Route path="/guests" element={<GuestCRM />} />
          <Route path="/waitlist" element={<WaitlistView />} />
          <Route path="/statistics" element={<Statistics />} />
          <Route path="/shifts" element={<ShiftSettings />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
};

export default Dashboard;
